/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  CouponTarget,
  Languages,
  VoucherTypes,
} from '@/constants'
import { useAuth, useCart, useStore } from '@/containers'
import { CouponArrowDown, CouponIconSimple, IconClose } from '@/styles/Icon'
import type { Cart, VoucherItem } from '@/types'
import { api } from '@/utils/api'
import { getTranslate } from '@/utils/api-interceptors'
import { convertCoupon } from '@/utils/next-util'
import {
  applyVoucherSmartOverride,
  enrichProductFallback,
  findProductInCart,
  getCouponCodeFromKey,
  isValidVoucherKey,
  normalizeVoucherItem,
  reconcileManualVouchers,
  resolveSelectionChange,
  sanitizeVoucherKeys,
} from '@/utils/voucher-util'
import { Dialog, Listbox, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { getCookie } from 'cookies-next'
import dayjs from 'dayjs'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import type { SetStateAction } from 'react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import ImageOptimize from '../ImageOptimize'
import FormRadioButtonItems from './FormRadioButtonItems'

interface FormDiscount {
  className?: string
  onClick?: () => void
  icon?: boolean
  disabled?: boolean
  isError?: boolean | string
  type?: 'button' | 'submit' | 'reset' | undefined
  typeButtonPrimary?: 'button' | 'submit' | 'reset' | undefined
  classButtonConfirm?: string
  id?: string
  name?: string
  value?: string
  primary?: boolean
  secondary?: boolean
  // onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder: string
  buttonPlaceholder: string
  buttonText: string
  shippingMethod?: string
  discount?: number
}

const FormDiscount = ({
  name = '',
  id = '',
  shippingMethod,
  placeholder = 'Mã giảm giá',
  buttonText = 'Áp dụng',
  discount = 0,
}: FormDiscount) => {
  const { selectedLang } = useStore()
  const {
    getCartCoupons,
    cartCoupons,
    setCouponApply,
    setIsPreOrder,
    couponApply,
  } = useCart()
  const {
    fetchCart,
    setErrorAuth,
    errorAuth,
    cart,
    voucherApply,
    setVoucherApply,
  } = useAuth()

  const [stagedSelected, setStagedSelected] = useState<string[]>(
    (voucherApply.length > 0 && voucherApply) || couponApply || []
  )
  const [manualVouchers, setManualVouchers] = useState<VoucherItem[]>([])
  // Lưu danh sách voucher nhập tay đã được cam kết (bấm Xác nhận)
  const [committedManualVouchers, setCommittedManualVouchers] = useState<
    VoucherItem[]
  >([])
  const [inputVoucher, setInputVoucher] = useState('')
  const [inputVoucherError, setInputVoucherError] = useState('')
  const [showListCoupons, setShowListCoupons] = useState(false)
  const [isApply, setIsApply] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)

  const t = useTranslations('Cart')
  const router = useRouter()

  useEffect(() => {
    getCartCoupons()
    setInputVoucher('')
    setInputVoucherError('')
    setIsApply(false)
    setErrorAuth({
      status: false,
      message: '',
      key: '',
    })
  }, [])

  // Đóng modal an toàn và revert voucher nhập tay chưa xác nhận
  const handleCloseModal = () => {
    setShowListCoupons(false)
    const reconciled = reconcileManualVouchers({
      cart,
      cartCouponsFromApi: cartCoupons,
      sessionCommittedManual: committedManualVouchers,
    })
    setManualVouchers(reconciled)
    setCommittedManualVouchers(reconciled)
    const applied =
      cart?.coupons
        ?.filter(e => !e.mes)
        ?.map(e => e.coupon?.initialKey)
        ?.filter(k => isValidVoucherKey(k)) || []
    const current = sanitizeVoucherKeys(
      applied.length > 0
        ? (applied as string[])
        : (voucherApply.length > 0 && voucherApply) || couponApply || []
    )
    setStagedSelected(current)
    setInputVoucher('')
    setInputVoucherError('')
    setIsApply(false)
  }

  // Đồng bộ Staging state mỗi khi mở popup
  useEffect(() => {
    if (showListCoupons) {
      getCartCoupons()
      const applied =
        cart?.coupons
          ?.filter(e => !e.mes)
          ?.map(e => e.coupon?.initialKey)
          ?.filter(k => isValidVoucherKey(k)) || []
      const current = sanitizeVoucherKeys(
        applied.length > 0
          ? (applied as string[])
          : (voucherApply.length > 0 && voucherApply) || couponApply || []
      )
      setStagedSelected(current)
      const reconciled = reconcileManualVouchers({
        cart,
        cartCouponsFromApi: cartCoupons,
        sessionCommittedManual: committedManualVouchers,
      })
      setManualVouchers(reconciled)
      setCommittedManualVouchers(reconciled)
      setInputVoucher('')
      setInputVoucherError('')
      setIsApply(false)
      setErrorAuth({
        status: false,
        message: '',
        key: '',
      })
    }
  }, [showListCoupons])

  useEffect(() => {
    if (router?.query?.type == 'preOrder') {
      setIsPreOrder(true)
    }
  }, [router])

  const currency = (lang: string) => {
    switch (lang) {
      case 'vi':
        return 'VND'
      case 'en':
        return 'USD'
      default:
        return 'VND'
    }
  }

  const handleChange = (event: {
    target: { value: SetStateAction<string> }
  }) => {
    const val = event.target.value as string
    setInputVoucher(val)
    setInputVoucherError('')
    setIsApply(Boolean(val && val.trim()))
  }

  const clearInputCoupon = () => {
    setInputVoucher('')
    setIsApply(false)
    setInputVoucherError('')
  }

  function checkVoucherType(voucher: VoucherItem) {
    switch (voucher.type) {
      case VoucherTypes.PERCENT:
        return voucher.percentValue + '%'
      case VoucherTypes.FIXED:
        return voucher.fixedValue
          ?.toLocaleString(selectedLang as string, {
            style: 'currency',
            currency: currency(selectedLang as string),
          })
          .replace(/\s₫/g, 'đ')
      default:
        break
    }
  }

  // Pre-flight Verification cho mã nhập tay
  const handleApplyManualCode = async () => {
    const rawCode = inputVoucher.trim().toUpperCase()
    if (!rawCode) return

    setInputVoucherError('')

    // Kiểm tra xem mã này đã được chọn trong stagedSelected chưa
    const isAlreadySelected = stagedSelected.some(
      key => getCouponCodeFromKey(key).toUpperCase() === rawCode
    )
    if (isAlreadySelected) {
      toast.info(
        getCookie('language') === Languages.EN
          ? 'This promotional code is already selected!'
          : 'Mã giảm giá này đang được chọn áp dụng!'
      )
      return
    }

    setIsVerifying(true)
    try {
      // Pre-flight cô lập: Chỉ thẩm định mã mới rawCode với giỏ hàng để tránh xung đột
      const params = new URLSearchParams()
      params.append('coupon', rawCode)
      if (shippingMethod) params.append('shippingMethod', shippingMethod)

      const res = await api(`api/shopping-cart?${params.toString()}`, {
        method: 'GET',
        baseURL: process.env.NEXT_PUBLIC_ECOMMERCE_API_URL,
      })

      if (res instanceof Error) {
        setInputVoucherError(res.message || 'Mã giảm giá không hợp lệ')
        return
      }

      const cartData: Cart = res?.data
      if (!cartData || !cartData.coupons) {
        setInputVoucherError('Không thể xác thực mã giảm giá')
        return
      }

      // Tìm các items trong res.coupons tương ứng với mã rawCode
      const matchedCoupons = cartData.coupons.filter(
        c =>
          (c.coupon?.code || (c.coupon as any)?.couponCode || '').toUpperCase() ===
          rawCode
      )

      if (!matchedCoupons.length) {
        setInputVoucherError(
          getCookie('language') === Languages.EN
            ? 'Promotional code is invalid'
            : 'Mã giảm giá không hợp lệ'
        )
        return
      }

      // Kiểm tra xem có item nào hợp lệ (!mes) không
      const validMatched = matchedCoupons.filter(c => !c.mes)
      if (!validMatched.length) {
        const errorMessage =
          matchedCoupons[0].mes ||
          'Mã giảm giá không áp dụng được cho đơn hàng này'
        setInputVoucherError(errorMessage)
        return
      }

      // Mã hợp lệ! Trích xuất thông tin
      const sample = validMatched[0].coupon
      const isOrder = sample.target === CouponTarget.ORDER
      const newManualItems: VoucherItem[] = []
      const applicableProductIds: string[] = []

      if (isOrder) {
        newManualItems.push({
          ...sample,
          code: rawCode,
          target: CouponTarget.ORDER,
        } as unknown as VoucherItem)
      } else {
        validMatched.forEach(item => {
          const pId =
            item.coupon?.initialKey?.split('_')?.[1] ||
            (item.coupon as any)?.targetId ||
            item.coupon?.product?._id
          if (pId && pId !== 'undefined') {
            applicableProductIds.push(pId)
            const prod =
              findProductInCart(cart, pId, cartCoupons) ||
              item.coupon?.product ||
              enrichProductFallback(pId, item.coupon)

            newManualItems.push({
              ...item.coupon,
              code: rawCode,
              target: CouponTarget.PRODUCT,
              product: prod,
            } as unknown as VoucherItem)
          }
        })
      }

      // 1. Thêm vào manualVouchers
      setManualVouchers(prev => [...prev, ...newManualItems])

      // 2. Chạy Smart Override Engine để cập nhật stagedSelected
      const nextSelected = applyVoucherSmartOverride({
        currentSelected: stagedSelected,
        newCouponCode: rawCode,
        target: isOrder ? 'order' : 'product',
        applicableProductIds,
      })
      setStagedSelected(nextSelected)

      // 3. Xóa trắng ô input & hiển thị toast thành công
      setInputVoucher('')
      setInputVoucherError('')
      setIsApply(false)

      toast.success(
        getCookie('language') === Languages.EN
          ? `Added code ${rawCode} successfully!`
          : `Đã áp dụng mã ${rawCode} thành công!`,
        {
          position: 'bottom-center',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          theme: 'light',
        }
      )
    } catch (err: any) {
      console.error('Pre-flight voucher error:', err)
      setInputVoucherError(err?.message || 'Có lỗi xảy ra khi xác thực mã')
    } finally {
      setIsVerifying(false)
    }
  }

  // Phase 2: Commit giỏ hàng khi nhấn "Xác nhận"
  const applyCoupon = async () => {
    setIsProcessing(true)
    try {
      const cleanSelected = sanitizeVoucherKeys(stagedSelected)
      await fetchCart({
        coupon: cleanSelected,
        shippingMethod,
        action: 'form discount apply',
      })
      setVoucherApply(cleanSelected)
      setCouponApply(cleanSelected)
      const selectedCodes = new Set(
        cleanSelected.map(k => getCouponCodeFromKey(k).toUpperCase())
      )
      const confirmedManual = manualVouchers.filter(m =>
        selectedCodes.has((m.code || (m as any).couponCode || '').toUpperCase())
      )
      setCommittedManualVouchers(confirmedManual)
      setManualVouchers(confirmedManual)
      setShowListCoupons(false)
      getCartCoupons()
      setErrorAuth({
        status: false,
        message: '',
        key: '',
      })
      toast.success(
        getCookie('language') === Languages.EN
          ? 'Apply promotional code successfully!'
          : 'Áp dụng mã khuyến mại thành công!',
        {
          position: 'bottom-center',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          theme: 'light',
        }
      )
    } catch (err: any) {
      setErrorAuth({
        status: true,
        message: err.message,
        key: 'FormDiscount',
      })
      toast.error(err?.message || 'Có lỗi xảy ra khi áp dụng mã khuyến mại')
    } finally {
      setIsProcessing(false)
    }
  }
  const allCoupons = useMemo(() => {
    const normalizedManual = manualVouchers.map(m => normalizeVoucherItem<VoucherItem>(m))
    const normalizedPublic = (cartCoupons || []).map(c => normalizeVoucherItem<VoucherItem>(c))
    const base = normalizedPublic
    const existingCodes = new Set(base.map(c => (c.code || (c as any).couponCode || '').trim().toUpperCase()))
    const uniqueManual = normalizedManual.filter(
      m => !existingCodes.has((m.code || (m as any).couponCode || '').trim().toUpperCase())
    )
    return [...uniqueManual, ...base]
  }, [cartCoupons, manualVouchers])

  const cardCouponsMapping = useMemo(() => {
    if (!allCoupons?.length) return []
    return allCoupons.reduce((acc: any, cur: VoucherItem) => {
      const couponCode = (cur.code || (cur as any).couponCode || '').trim().toUpperCase()
      if (cur.target === CouponTarget.PRODUCT) {
        const prodId =
          cur.product?._id || (cur as any)?.targetId || (cur as any)?.productId
        if (!prodId || prodId === 'undefined') return acc

        const productInfo =
          cur.product ||
          findProductInCart(cart, prodId, cartCoupons) ||
          enrichProductFallback(prodId, cur)

        const productGroup = acc.find((e: any) => e._id === prodId)
        const couponApply = cart?.coupons?.find(
          coupon =>
            coupon.coupon?.initialKey ===
            convertCoupon(couponCode, prodId)
        )
        if (productGroup) {
          productGroup.coupons.push({
            ...cur,
            code: couponCode,
            product: productInfo,
            ...(couponApply && {
              mes: couponApply.mes,
            }),
          })
          return acc
        }
        return [
          ...acc,
          {
            ...productInfo,
            _id: prodId,
            coupons: [
              {
                ...cur,
                code: couponCode,
                product: productInfo,
                ...(couponApply && {
                  mes: couponApply.mes,
                }),
              },
            ],
          },
        ]
      }

      const couponApply = cart?.coupons?.find(
        coupon => coupon.coupon?.initialKey === `${couponCode}_orderCoupon`
      )
      return [
        ...acc,
        {
          name: {
            vi: 'Áp dụng cho đơn hàng',
          },
          _id: 'orderCoupon',
          coupons: [
            {
              ...cur,
              code: couponCode,
              ...(couponApply && {
                mes: couponApply.mes,
              }),
            },
          ],
        },
      ]
    }, [] as any)
  }, [allCoupons, cart])

  const couponOnchange = (value: string[]) => {
    const next = resolveSelectionChange(stagedSelected, value)
    setStagedSelected(next)
  }

  return (
    <div className='discountBox relative' id='voucher'>
      <div
        className=' relative flex h-[48px] w-full cursor-pointer flex-row items-center border border-[#E5E5E5] rounded bg-white'
        onClick={() => setShowListCoupons(true)}
      >
        <span className='icon mx-[15px]'>
          <CouponIconSimple />
        </span>
        <span className='text-Headline leading-none tracking-tight'>
          Sử dụng mã giảm giá
        </span>
        <CouponArrowDown className='absolute right-[15px] top-[35%]' />
      </div>
      {cart?.coupons?.filter(e => e.mes).length > 0 && (
        <div className='my-1 w-full text-sm font-normal italic leading-tight text-red opacity-80'>
          Một trong số các voucher không hợp lệ, tiếp tục mua hàng mà không cần
          voucher đó
        </div>
      )}
      {discount && discount > 0 ? (
        <div className='my-1'>
          Đã áp dụng <strong>{cart.coupons.filter(e => !e.mes).length}</strong>{' '}
          mã (giảm{' '}
          <strong className='font-semibold text-red'>
            {discount
              ?.toLocaleString(selectedLang as string, {
                style: 'currency',
                currency: currency(selectedLang as string),
              })
              .replace(/\s₫/g, 'đ')}
          </strong>
          )
        </div>
      ) : (
        ''
      )}
      <div className='w-full overflow-y-auto'>
        <FormRadioButtonItems className='flex flex-wrap !gap-2 '>
          {!errorAuth?.status &&
            cart?.coupons &&
            cart.coupons
              .filter(e => !e.mes)
              .map((e, i) => {
                return (
                  <div
                    key={`cr-${i}`}
                    className={`voucherItem flex h-[38px] w-full max-w-[135px] justify-center  bg-[url('/assets/icons/couponBg.svg')] bg-cover bg-no-repeat`}
                  >
                    <span className='self-center text-Voucher text-[#DC2626]'>
                      Giảm{' '}
                      {e.value
                        .toLocaleString(Languages.VN, {
                          style: 'currency',
                          currency: 'VND',
                        })
                        .replace(/\s₫/g, 'đ')}
                    </span>
                  </div>
                )
              })}
        </FormRadioButtonItems>
      </div>
      <Transition appear show={showListCoupons} as={Fragment}>
        <Dialog
          as='div'
          className={`relative z-[9999]`}
          onClose={handleCloseModal}
        >
          <Transition.Child
            as={Fragment}
            enter='ease-in-out duration-500'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in-out duration-500'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='fixed inset-0 left-0 bottom-0 bg-black-150 transition-opacity' />
          </Transition.Child>
          <div className='pointer-events-none z-50 w-full overflow-hidden fixed left-[50%] translate-x-[-50%] rounded-tr-sm rounded-tl-sm bottom-0 flex max-h-[60vh] h-full max-w-[440px] '>
            <Transition.Child
              as={Fragment}
              enter='transform transition ease-in-out duration-500'
              enterFrom='translate-y-[70%]'
              enterTo='translate-y-[0%]'
              leave='transform transition ease-in-out duration-500'
              leaveFrom='translate-y-[0%]'
              leaveTo='translate-y-[100%]'
            >
              <Dialog.Panel className='pointer-events-auto w-full max-w-[440px] h-full shadow-modal'>
                <div
                  className={`bg-white pb-6 block transform overflow-hidden align-middle shadow-modal w-full`}
                >
                  <div
                    className={`headerModal relative w-full px-2 pt-4 h-[60px]`}
                  >
                    <Dialog.Title as='h2' className={`text-titleSection mt-1`}>
                      {t('voucherForYou')}
                    </Dialog.Title>

                    <button
                      onClick={handleCloseModal}
                      className='absolute right-3 top-4 cursor-pointer z-10'
                    >
                      <IconClose />
                    </button>
                  </div>
                  <div className='mb-2 flex items-center justify-between px-2 gap-2'>
                    <div className='inputGroup relative flex-1'>
                      <input
                        placeholder={placeholder}
                        type='text'
                        name={name}
                        id={id}
                        value={inputVoucher}
                        onChange={handleChange}
                        className={clsx(
                          inputVoucherError && 'border-red',
                          'w-full h-11 rounded-lg border border-grey p-3 text-base text-fieldInput font-bold text-blue shadow-none placeholder:text-dark-400/70 placeholder:text-sm placeholder:font-normal focus:border-grey focus:shadow-none focus:outline-none focus:ring-0'
                        )}
                      />
                      {inputVoucher ? (
                        <button
                          className='clear absolute right-3 top-[50%] z-10 translate-y-[-50%]'
                          type='button'
                          onClick={() => clearInputCoupon()}
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width={20}
                            height={20}
                            viewBox='0 0 20 20'
                            fill='none'
                          >
                            <circle cx={10} cy={10} r='9.5' fill='#B1B1B1' />
                            <path
                              d='M14.0716 12.7695C14.4319 13.1299 14.4319 13.7123 14.0716 14.0728C13.8919 14.2525 13.656 14.3428 13.4199 14.3428C13.184 14.3428 12.9481 14.2525 12.7683 14.0728L9.99902 11.3032L7.2297 14.0728C7.04998 14.2525 6.81407 14.3428 6.57816 14.3428C6.34208 14.3428 6.10617 14.2525 5.92646 14.0728C5.56618 13.7123 5.56618 13.1299 5.92646 12.7695L8.69595 10.0001L5.92646 7.23071C5.56618 6.87026 5.56618 6.28789 5.92646 5.92744C6.2869 5.56716 6.86925 5.56716 7.2297 5.92744L9.99902 8.697L12.7683 5.92744C13.1288 5.56716 13.7111 5.56716 14.0716 5.92744C14.4319 6.28789 14.4319 6.87026 14.0716 7.23071L11.3021 10.0001L14.0716 12.7695Z'
                              fill='white'
                            />
                          </svg>
                        </button>
                      ) : (
                        ''
                      )}
                    </div>
                    <button
                      className='h-11 max-w-[110px] flex-[110px] uppercase text-sm rounded-lg bg-orange px-[10px] py-0 font-bold text-white disabled:bg-gray-1000 disabled:outline-none'
                      disabled={!isApply || isVerifying}
                      type='button'
                      onClick={handleApplyManualCode}
                    >
                      {isVerifying ? '...' : buttonText}
                    </button>
                  </div>
                  {inputVoucherError && (
                    <div className='mb-1 ml-3 w-full text-mainBody font-normal leading-tight text-red opacity-80'>
                      {inputVoucherError}
                    </div>
                  )}
                  <p className='px-3 h-12 leading-snug text-sm font-normal text-neutral-600'>
                    {t('voucherSubTitle')}
                  </p>
                  <div
                    className={`max-h-[calc(60vh-180px)] min-h-[calc(60vh-180px)] max-w-[calc(100%-8px)] mr-2 mb-5 pl-3 pr-2 pb-20 h-full scrollBarCS`}
                  >
                    {cardCouponsMapping && cardCouponsMapping.length > 0 ? (
                      <Listbox
                        value={stagedSelected}
                        onChange={async (e: string[]) => couponOnchange(e)}
                        multiple
                      >
                        <Listbox.Options
                          as='div'
                          static
                          className={`flex w-full cursor-pointer flex-wrap items-start justify-start overflow-auto rounded-md bg-white text-base`}
                        >
                          <div>
                             {cardCouponsMapping.filter((item: { _id: string }) => item._id !== 'orderCoupon').map((e: any) => (
                              <div key={e._id}>
                                <p className={clsx(`mb-3 text-sm`)}>
                                  <strong>Sản phẩm áp dụng: </strong>{' '}
                                    {getTranslate(e.name) || e.code || e._id || ''}
                                </p>
                                <div className='ml-[40px] flex flex-wrap gap-2 pb-2'>
                                  {e.coupons &&
                                    e.coupons.map((c: VoucherItem) => (
                                      <Listbox.Option
                                        as='div'
                                        key={c._id}
                                        className={({ selected }) =>
                                           clsx(
                                             `middle relative mr-1 w-full cursor-pointer text-fieldInput before:absolute before:left-[-40px] before:top-1/2 before:-translate-y-1/2 before:transform`,
                                             c.mes
                                               ? `!text-yellow-500 before:content-[url('/assets/icons/couponWarning.svg')]`
                                               : selected
                                               ? `before:content-[url('/assets/icons/couponChecked.svg')]`
                                               : `before:content-[url('/assets/icons/couponPending.svg')]`
                                           )
                                         }
                                         value={`${(c.code || (c as any).couponCode || '').trim().toUpperCase()}_${e._id}`}
                                       >
                                         <div
                                           className={clsx(
                                             'voucherItem flex h-20 w-full items-center overflow-hidden rounded-lg bg-red-600 bg-opacity-5 leading-snug',
                                             c.mes && '!bg-yellow-500/10'
                                           )}
                                         >
                                           <div className='voucherLeft min-w-[120px] max-w-[30%] flex-[30%]'>
                                             <div className='flex w-full flex-wrap justify-center'>
                                               <p
                                                 className={clsx(
                                                  'w-full text-center text-sm font-semibold text-[#DC2626]',
                                                    c.mes && '!text-yellow-500'
                                                 )}
                                               >
                                                 Giảm
                                               </p>
                                               <p
                                                 className={clsx(
                                                   'w-full text-center text-base font-semibold text-[#DC2626] flex flex-col',
                                                   c.mes && '!text-yellow-500'
                                                 )}
                                               >
                                                 <span>{checkVoucherType(c)} </span>
                                                 <span className='text-xs italic font-normal'>{`(${c.code || (c as any).couponCode || ''})`}</span>
                                               </p>
                                             </div>
                                           </div>
                                           <div className='voucherRight max-w-[70%] flex-[70%]'>
                                            <div className='flex w-full flex-wrap items-center pl-3 leading-4'>
                                              <p
                                                className={clsx(
                                                  'mb-1 w-full text-sm font-bold text-red-500',
                                                  c.mes && '!text-yellow-500'
                                                )}
                                              >
                                                {dayjs(c.startTime).format(
                                                  'HH:mm DD.MM'
                                                )}{' '}
                                                ~{' '}
                                                {dayjs(c.endTime).format(
                                                  'HH:mm DD.MM'
                                                )}
                                              </p>
                                              <div
                                                className={clsx(
                                                  'mb-0 w-full text-xs font-normal text-[#DC2626]',
                                                  c.mes && '!text-yellow-500'
                                                )}
                                              >
                                                {getTranslate(c.description)}
                                              </div>
                                              {c.minValue ? (
                                                <div
                                                  className={clsx(
                                                    'flex text-xs font-normal text-[#DC2626] pr-1',
                                                    c.mes && '!text-yellow-500'
                                                  )}
                                                >
                                                  <span>
                                                    {t('minValueLabel')}
                                                  </span>{' '}
                                                  <span className='ml-[4px] font-semibold'>
                                                    {c.minValue
                                                      .toLocaleString(
                                                        selectedLang as string,
                                                        {
                                                          style: 'currency',
                                                          currency: currency(
                                                            selectedLang as string
                                                          ),
                                                        }
                                                      )
                                                      .replace(/\s₫/g, 'đ')}
                                                  </span>
                                                </div>
                                              ) : (
                                                ''
                                              )}
                                              {c.maxDiscountValue &&
                                                c.type !==
                                                  VoucherTypes.FIXED && (
                                                  <div
                                                    className={clsx(
                                                      'flex text-xs font-normal text-[#DC2626]',
                                                      c.mes &&
                                                        '!text-yellow-500'
                                                    )}
                                                  >
                                                    <span>
                                                      {t('maxDiscountValue')}
                                                    </span>{' '}
                                                    <span className='ml-[4px] font-semibold'>
                                                      {c.maxDiscountValue
                                                        .toLocaleString(
                                                          selectedLang as string,
                                                          {
                                                            style: 'currency',
                                                            currency: currency(
                                                              selectedLang as string
                                                            ),
                                                          }
                                                        )
                                                        .replace(/\s₫/g, 'đ')}
                                                    </span>
                                                  </div>
                                                )}
                                            </div>
                                          </div>
                                        </div>
                                        {c.mes && <span>{c.mes}</span>}
                                      </Listbox.Option>
                                    ))}
                                </div>
                              </div>
                            ))}
                            {/*  apply for order */}
                            <div className='pt-2'>
                              <p
                                  className={clsx(
                                    `mb-3 `
                                  )}
                                >
                                  <div className='text-sm'>
                                    <span className='font-bold text-sm'>Áp dụng cho đơn hàng</span>
                                  </div>
                                </p>
                            </div>
                            {cardCouponsMapping.filter((item: { _id: string }) => item._id == 'orderCoupon').map((e: any) => (
                              <div key={`lc-${e._id}`}>
                                <div className='ml-[40px] flex flex-wrap gap-2 pb-2'>
                                  {e.coupons &&
                                    e.coupons.map((c: VoucherItem) => (
                                      <Listbox.Option
                                        as='div'
                                        key={c._id}
                                        className={({ selected }) =>
                                          clsx(
                                            `middle relative mr-1 w-full cursor-pointer text-fieldInput before:absolute before:left-[-40px] before:top-1/2 before:-translate-y-1/2 before:transform`,
                                            c.mes
                                              ? `!text-yellow-500 before:content-[url('/assets/icons/couponWarning.svg')]`
                                              : selected
                                              ? `before:content-[url('/assets/icons/couponChecked.svg')]`
                                              : `before:content-[url('/assets/icons/couponPending.svg')]`
                                          )
                                        }
                                        value={`${(c.code || (c as any).couponCode || '').trim().toUpperCase()}_${e._id}`}
                                      >
                                        <div
                                          className={clsx(
                                            'voucherItem flex h-20 w-full items-center overflow-hidden rounded-lg bg-[#DC262633]/5',
                                            c.mes && '!bg-yellow-500/10'
                                          )}
                                        >
                                          <div className='voucherLeft min-w-[120px] max-w-[30%] flex-[30%]'>
                                            <div className='flex w-full flex-wrap justify-center'>
                                              <p
                                                className={clsx(
                                                  'w-full text-center text-sm font-semibold text-[#DC2626]',
                                                  c.mes && '!text-yellow-500'
                                                )}
                                              >
                                                Giảm
                                              </p>
                                              <p
                                                className={clsx(
                                                  'w-full text-center text-base font-semibold text-[#DC2626] flex flex-col',
                                                  c.mes && '!text-yellow-500'
                                                )}
                                              >
                                                <span>{checkVoucherType(c)} </span>
                                                <span className='text-xs italic font-normal'>{`(${c.code || (c as any).couponCode || ''})`}</span>
                                              </p>
                                              
                                            </div>
                                          </div>
                                          <div className='voucherRight max-w-[70%] flex-[70%]'>
                                            <div className='flex w-full flex-wrap items-center pl-3 leading-4'>
                                              <p
                                                className={clsx(
                                                  'mb-1 w-full text-sm font-bold text-red-500',
                                                  c.mes && '!text-yellow-500'
                                                )}
                                              >
                                                {dayjs(c.startTime).format(
                                                  'HH:mm DD.MM'
                                                )}{' '}
                                                ~{' '}
                                                {dayjs(c.endTime).format(
                                                  'HH:mm DD.MM'
                                                )}
                                              </p>
                                              <div
                                                className={clsx(
                                                  'mb-0 w-full text-xs font-normal text-[#DC2626]',
                                                  c.mes && '!text-yellow-500'
                                                )}
                                              >
                                                {getTranslate(c.description)}
                                              </div>
                                              {c.minValue ? (
                                                <div
                                                  className={clsx(
                                                    'flex text-xs font-normal text-[#DC2626] pr-1',
                                                    c.mes && '!text-yellow-500'
                                                  )}
                                                >
                                                  <span>
                                                    {t('minValueLabel')}
                                                  </span>{' '}
                                                  <span className='ml-[4px] font-semibold'>
                                                    {c.minValue
                                                      .toLocaleString(
                                                        selectedLang as string,
                                                        {
                                                          style: 'currency',
                                                          currency: currency(
                                                            selectedLang as string
                                                          ),
                                                        }
                                                      )
                                                      .replace(/\s₫/g, 'đ')}
                                                  </span>
                                                </div>
                                              ) : (
                                                ''
                                              )}
                                              {c.maxDiscountValue &&
                                                c.type !==
                                                  VoucherTypes.FIXED && (
                                                  <div
                                                    className={clsx(
                                                      'flex text-xs font-normal text-[#DC2626]',
                                                      c.mes &&
                                                        '!text-yellow-500'
                                                    )}
                                                  >
                                                    <span>
                                                      {t('maxDiscountValue')}
                                                    </span>{' '}
                                                    <span className='ml-[4px] font-semibold'>
                                                      {c.maxDiscountValue
                                                        .toLocaleString(
                                                          selectedLang as string,
                                                          {
                                                            style: 'currency',
                                                            currency: currency(
                                                              selectedLang as string
                                                            ),
                                                          }
                                                        )
                                                        .replace(/\s₫/g, 'đ')}
                                                    </span>
                                                  </div>
                                                )}
                                            </div>
                                          </div>
                                        </div>
                                        {c.mes && <span>{c.mes}</span>}
                                      </Listbox.Option>
                                    ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </Listbox.Options>
                      </Listbox>
                    ) : (
                      <div className='emptyVoucher flex w-full flex-wrap justify-center'>
                        <ImageOptimize
                          src={'../empty-image.png'}
                          width={240}
                          height={240}
                          alt='Empty'
                        />
                        <div className='w-full text-center text-base font-normal leading-7 text-neutral-600'>
                          Chưa có mã giảm giá phù hợp
                        </div>
                      </div>
                    )}
                      
                  </div>
                  <div className='flex w-full h-[90px] bg-white z-50 fixed bottom-0'>
                    <button
                      className='mx-auto mt-2 h-11 w-[70%] uppercase rounded-lg bg-orange px-[10px] py-0 font-bold text-white disabled:bg-gray-1000 disabled:outline-none'
                      disabled={isProcessing}
                      type='button'
                      onClick={() => applyCoupon()}
                    >
                      {isProcessing ? 'Đang xử lý...' : 'Xác nhận'}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}

export default FormDiscount
